import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Button,
  Checkbox,
  SearchField,
  Spinner,
  MenuBoxIcon,
} from '@noor/ui';
import { groupDigits, useDebouncedCallback } from '@noor/lib';
import { useMerchantProducts, type MenuProduct } from '@/entities/menu';

interface ProductPickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Product ids already in the step-2 list — pre-ticked and re-committable. */
  selectedIds: string[];
  /** Commits the picked set. Fired by the footer button, never per tap. */
  onConfirm: (products: MenuProduct[]) => void;
}

/**
 * «Выберите продукт» — the branch wizard's product picker (Figma 1067:13717).
 *
 * A 514px dialog over the wizard: a search box, a «Выбрать все» master
 * checkbox, then the merchant's catalog as 64px-thumbnail rows with a trailing
 * checkbox, and a footer button that counts what is picked out of what is
 * listed («Выбрать 23 из 100»).
 *
 * The catalog is the MERCHANT-wide one, not the branch's menu: a picker scoped
 * to the branch being edited could only offer products it already stocks,
 * which is the opposite of what the button is for.
 *
 * Selection is LOCAL until the footer commits it — the design has no per-tap
 * commit and no cancel beyond the close X, so dismissing must leave the list
 * as it was. The dialog is mounted only while open (see `ProductStep`), so
 * each open seeds from the real list rather than an abandoned draft.
 */
export function ProductPicker({
  open,
  onOpenChange,
  selectedIds,
  onConfirm,
}: ProductPickerProps) {
  const { t } = useTranslation('eats');
  // Two pieces of state, not one: `search` is what the box shows on every
  // keystroke, `query` is what the API is actually asked for. The catalog is
  // unbounded so the search is server-side, and debouncing the QUERY keeps the
  // field responsive without a request per character.
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const commitQuery = useDebouncedCallback(setQuery, 300);
  const { data: products, isPending } = useMerchantProducts(query);

  const knownProductsRef = useRef<Map<string, MenuProduct>>(new Map());
  useEffect(() => {
    if (products) {
      for (const p of products) {
        knownProductsRef.current.set(String(p.id), p);
      }
    }
  }, [products]);

  function handleSearch(next: string): void {
    setSearch(next);
    commitQuery(next);
  }

  // Seeded once per mount from what the step-2 list already holds, so reopening
  // the picker shows the current selection ticked.
  const [picked, setPicked] = useState<ReadonlySet<string>>(() => new Set(selectedIds));

  const rows = products ?? [];
  const pickedCount = picked.size;
  // "All" means all of what is CURRENTLY listed — under an active search that
  // is the filtered set, which is what the merchant can actually see and tick.
  const allPicked = rows.length > 0 && rows.every((row) => picked.has(String(row.id)));

  function toggle(productId: string): void {
    setPicked((current) => {
      const next = new Set(current);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
      }
      return next;
    });
  }

  function toggleAll(): void {
    setPicked((current) => {
      const next = new Set(current);
      for (const row of rows) {
        const id = String(row.id);
        // Ticking "all" adds the listed rows; unticking removes only those,
        // leaving a selection made under a different search untouched.
        if (allPicked) next.delete(id);
        else next.add(id);
      }
      return next;
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={
          'flex max-h-[calc(100dvh-4rem)] max-w-[32.125rem] flex-col gap-0 ' +
          'overflow-hidden rounded-3xl p-0 sm:max-w-[32.125rem] ' +
          '[&>[data-slot=dialog-header]]:shrink-0 [&>[data-slot=dialog-header]]:px-6 ' +
          '[&>[data-slot=dialog-header]]:py-5 [&>[data-slot=dialog-header]]:pr-16 ' +
          '[&>[data-slot=dialog-header]]:border-b [&>[data-slot=dialog-header]]:border-border ' +
          '[&>[data-slot=dialog-header]_[data-slot=dialog-description]]:sr-only ' +
          '[&>button]:top-5 [&>button]:right-6'
        }
      >
        <DialogHeader>
          <DialogTitle asChild>
            {/* The same orange 48px featured tile the wizard's own header uses. */}
            <span className="flex items-center gap-4">
              <span className="flex size-12 shrink-0 items-center justify-center rounded-3xl bg-primary text-primary-foreground shadow-xs">
                <MenuBoxIcon className="size-6" />
              </span>
              <span className="text-[1.375rem] font-semibold tracking-tight text-foreground">
                {t('branches.form.products.pickTitle')}
              </span>
            </span>
          </DialogTitle>
          <DialogDescription>{t('branches.form.products.pickTitle')}</DialogDescription>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden px-5 pt-5">
          <SearchField
            value={search}
            onChange={(event) => handleSearch(event.target.value)}
            onClear={() => handleSearch('')}
            placeholder={t('branches.form.products.searchPlaceholder')}
            className="shrink-0 [&_[data-slot=search-field-input]]:h-11 [&_[data-slot=search-field-input]]:rounded-3xl [&_[data-slot=search-field-input]]:border-input [&_[data-slot=search-field-input]]:bg-card [&_[data-slot=search-field-input]]:text-sm [&_[data-slot=search-field-input]]:shadow-xs"
          />

          {/* «Выбрать все» — 24px box, 12px caption, 4px apart. */}
          <label className="flex shrink-0 cursor-pointer items-center gap-1 self-start">
            <Checkbox
              checked={allPicked}
              onCheckedChange={toggleAll}
              disabled={rows.length === 0}
              className="size-6 rounded-[8px]"
            />
            <span className="text-xs font-medium tracking-tight text-foreground">
              {t('branches.form.products.selectAll')}
            </span>
          </label>

          <div className="min-h-0 flex-1 overflow-y-auto pb-2">
            {isPending ? (
              <div className="flex justify-center py-10">
                <Spinner />
              </div>
            ) : rows.length === 0 ? (
              <p className="py-10 text-center text-sm tracking-tight text-muted-foreground">
                {t('branches.form.products.empty')}
              </p>
            ) : (
              <ul className="flex flex-col">
                {rows.map((product) => {
                  const id = String(product.id);
                  const isPicked = picked.has(id);
                  return (
                    <li key={id} className="flex flex-col gap-[13px] pt-4 first:pt-0">
                      <label className="flex cursor-pointer items-center justify-between gap-3">
                        <span className="flex min-w-0 items-center gap-3">
                          {/* 64px tile, 12px radius — a grey placeholder when
                           * the product has no photo, never a broken image. */}
                          <span className="size-16 shrink-0 overflow-hidden rounded-xl bg-muted">
                            {product.imageUrl ? (
                              <img
                                src={product.imageUrl}
                                alt=""
                                className="size-full object-cover"
                              />
                            ) : null}
                          </span>
                          <span className="flex min-w-0 flex-col gap-2">
                            <span className="truncate text-base font-medium tracking-tight text-foreground">
                              {product.name}
                            </span>
                            <span className="text-sm font-medium tracking-tight text-foreground">
                              {groupDigits(String(product.price))}{' '}
                              {t('branches.form.currencySuffix')}
                            </span>
                          </span>
                        </span>
                        <Checkbox
                          checked={isPicked}
                          onCheckedChange={() => toggle(id)}
                          className="size-6 shrink-0 rounded-[8px]"
                        />
                      </label>
                      <span aria-hidden="true" className="h-px w-full bg-border" />
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        {/* The footer floats over the list on its own shadow, as the design
         * draws it — the rows scroll under it rather than stopping above it. */}
        <div className="shrink-0 bg-card px-6 pt-2 pb-6 shadow-lg">
          <Button
            type="button"
            size="lg"
            className="h-12 w-full rounded-full text-base font-semibold"
            disabled={rows.length === 0}
            onClick={() => {
              const selected: MenuProduct[] = [];
              for (const id of picked) {
                const found = knownProductsRef.current.get(id);
                if (found) selected.push(found);
              }
              onConfirm(selected);
            }}
          >
            {t('branches.form.products.confirm', {
              count: pickedCount,
              total: rows.length,
            })}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
